import { useState, useCallback, useRef, useMemo } from "react";
import { usePrivyWallet } from "@/hooks/usePrivyWallet";
import { createUmiInstance } from "@/lib/umi";
import { DigitalAsset, Trait } from "@/app/types";
import { message } from "antd";
import { PublicKey } from "@solana/web3.js";
import { fromWeb3JsPublicKey } from "@metaplex-foundation/umi-web3js-adapters";
import {
	LARGE_COLLECTION_THRESHOLD,
	CHUNK_SIZES,
	PROCESSING_DELAYS,
	API_LIMITS,
} from "@/constants/cnft.constants";

interface FetchProgress {
	strategy: "count" | "fetch" | "complete";
	progress: number;
	totalCount: number;
	errorCount: number;
}

interface ProcessedResult {
	processedNFTs: DigitalAsset[];
	traits: Trait[];
	errorCount: number;
}

interface UseOptimizedCNFTFetchResult {
	fetchCompressedNFTs: (silent?: boolean) => Promise<DigitalAsset[]>;
	cancelFetch: () => void;
	progress: FetchProgress;
	isLoading: boolean;
}

export const useOptimizedCNFTFetch = (
	safelyExtractMetadata: (item: any) => Promise<DigitalAsset | null>,
	addUniqueTraits: (traits: Trait[]) => void,
	silent: boolean = false
): UseOptimizedCNFTFetchResult => {
	const { signer, publicKey } = usePrivyWallet();

	const [isLoading, setIsLoading] = useState(false);
	const [progress, setProgress] = useState<FetchProgress>({
		strategy: "count",
		progress: 0,
		totalCount: 0,
		errorCount: 0,
	});

	// Refs for memory management
	const abortControllerRef = useRef<AbortController | null>(null);
	const processedNFTsRef = useRef<DigitalAsset[]>([]);
	const isProcessingRef = useRef(false);
	const cancelledRef = useRef(false);

	// Memoize RPC endpoint to prevent recreation
	const rpcEndpoint = useMemo(
		() =>
			process.env.NEXT_PUBLIC_ENV === "local"
				? process.env.NEXT_PUBLIC_DEVELOPMENT_RPC
				: process.env.NEXT_PUBLIC_PRODUCTION_RPC,
		[]
	);

	// Helper function to check if a value should be filtered out
	const shouldFilterOutTrait = (value: string): boolean => {
		const trimmed = value.trim();

		if (!trimmed || trimmed === "null" || trimmed === "NULL") {
			return true;
		}
		if (
			!isNaN(Number(trimmed)) &&
			!isNaN(parseFloat(trimmed)) &&
			isFinite(Number(trimmed))
		) {
			return true;
		}

		if (/^\d+$/.test(trimmed)) {
			return true;
		}

		if (/^\d+\.\d+$/.test(trimmed)) {
			return true;
		}

		if (trimmed.toLowerCase() === "true" || trimmed.toLowerCase() === "false") {
			return true;
		}

		if (/^(https?:\/\/|ftp:\/\/|@)/.test(trimmed.toLowerCase())) {
			return true;
		}

		if (
			/^\d+(\.\d+)?\s*(USD[TC]?|EUR|BTC|ETH|SOL|USDT|USDC|\$|€|£|¥)$/i.test(
				trimmed
			)
		) {
			return true;
		}

		if (
			/^\d+\s*(hours?|mins?|minutes?|secs?|seconds?|days?|hrs?|h|m|s)(\s+\d+\s*(hours?|mins?|minutes?|secs?|seconds?|days?|hrs?|h|m|s))*[!\.]?$/i.test(
				trimmed
			)
		) {
			return true;
		}

		if (/^\d{1,3}(,\d{3})*(\.\d+)?$/.test(trimmed)) {
			return true;
		}

		if (/^\d+(\.\d+)?[KMBTkmbt]$/i.test(trimmed)) {
			return true;
		}

		if (
			/^[a-zA-Z0-9-]+\.(com|org|net|io|xyz|app|co|me|tv|gg|ly|dev|ai|tech)$/i.test(
				trimmed
			)
		) {
			return true;
		}

		return false;
	};

	const processNFTsInChunks = useCallback(
		async (nfts: any[], chunkSize: number = 50): Promise<ProcessedResult> => {
			const allNFTs: DigitalAsset[] = [];
			const allTraits: Trait[] = [];
			let currentErrorCount = 0;

		for (let i = 0; i < nfts.length; i += chunkSize) {
			if (cancelledRef.current) break;
			const chunk = nfts.slice(i, i + chunkSize);

				// Process chunk
				const chunkPromises = chunk.map(async (item: any) => {
					try {
						const processed = await safelyExtractMetadata(item);
						if (processed) {
							// Extract traits
							const attributes = processed?.metadata_uri?.attributes;
							if (Array.isArray(attributes)) {
								const validTraits = attributes.filter((trait) => {
									if (
										!trait ||
										typeof trait.trait_type !== "string" ||
										typeof trait.value !== "string"
									) {
										return false;
									}

									const trimmedValue = trait.value.trim();
									// Filter out numeric values and nulls
									if (shouldFilterOutTrait(trimmedValue)) {
										return false;
									}

									return true;
								});
								return { processed, traits: validTraits };
							}
							return { processed, traits: [] };
						} else {
							return { processed: null, traits: [] };
						}
					} catch (error) {
						console.warn("Error processing NFT:", error);
						return { processed: null, traits: [] };
					}
				});

				const chunkResults = await Promise.all(chunkPromises);

				// Process results
				chunkResults.forEach((result) => {
					if (result.processed) {
						allNFTs.push(result.processed);
						allTraits.push(...result.traits);
					} else {
						currentErrorCount++;
					}
				});

				// Update progress
				const progressPercent = Math.min(
					((i + chunkSize) / nfts.length) * 100,
					100
				);
				setProgress((prev) => ({ ...prev, progress: progressPercent }));

				// Small delay to prevent UI blocking
				if (i + chunkSize < nfts.length) {
					await new Promise((resolve) =>
						setTimeout(resolve, PROCESSING_DELAYS.CHUNK_DELAY)
					);
				}
			}

			return {
				processedNFTs: allNFTs,
				traits: allTraits,
				errorCount: currentErrorCount,
			};
		},
		[safelyExtractMetadata]
	);

	// Fallback method for when UMI fails
	const fetchFallbackMethod = useCallback(async (suppressMessages: boolean = false): Promise<DigitalAsset[]> => {
		try {
			const response = await fetch(
				`${rpcEndpoint}/v0/addresses/${publicKey!.toString()}/nfts?compression=true`
			);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data = await response.json();
			const compressedNFTs = data.result?.nfts || [];
			// Update total count
			setProgress((prev) => ({
				...prev,
				totalCount: compressedNFTs.length,
			}));

			// Process fallback data in chunks
			const { processedNFTs, traits, errorCount } = await processNFTsInChunks(
				compressedNFTs,
				CHUNK_SIZES.STANDARD
			);

			addUniqueTraits(traits);
			setProgress((prev) => ({ ...prev, errorCount }));

			if (!suppressMessages) {
				if (processedNFTs.length > 0) {
					message.success(`Loaded ${processedNFTs.length} compressed NFTs`);
				} else {
					message.info("No compressed NFTs found in your wallet");
				}
			}

			if (errorCount > 0) {
				console.warn(
					`${errorCount} compressed NFTs had invalid metadata and were filtered out`
				);
			}

			return processedNFTs;
		} catch (fallbackError) {
			console.error("Fallback method also failed:", fallbackError);
			throw fallbackError;
		}
	}, [publicKey, rpcEndpoint, processNFTsInChunks, addUniqueTraits]);

	// Simple unified fetch function that handles both small and large collections
	const fetchAllCompressedNFTs = useCallback(async (suppressMessages: boolean = false): Promise<
		DigitalAsset[]
	> => {
		if (!signer || !publicKey) return [];
		try {
			// Create a proper signer with converted publicKey
			const umiSigner = {
				...signer,
				publicKey: fromWeb3JsPublicKey(new PublicKey(publicKey)),
			};
			const umi = createUmiInstance(umiSigner);
			let allAssets: any[] = [];
			let page = 1;
			let hasMore = true;
			let totalFetched = 0;
		while (hasMore && !cancelledRef.current) {
			try {
				// @ts-ignore - UMI types might not be fully updated
				const rpcAssetList = await umi.rpc.getAssetsByOwner({
						owner: publicKey!.toString(),
						limit: API_LIMITS.FETCH_LIMIT,
						page: page,
					});

					const fetchedAssets = rpcAssetList.items || [];
					const fetchedCount = fetchedAssets.length;
					allAssets.push(...fetchedAssets);
					totalFetched += fetchedCount;

					// Update progress based on fetched count
					setProgress((prev) => ({
						...prev,
						progress: Math.min(
							(totalFetched / Math.max(100, totalFetched)) * 80,
							60
						),
						totalCount: totalFetched,
					}));

					// If we got less than the limit, we've reached the end
					if (fetchedCount < API_LIMITS.FETCH_LIMIT) {
						hasMore = false;
					} else {
						page++;
					}

					// Add a small delay between pagination requests
					if (hasMore) {
						await new Promise((resolve) => setTimeout(resolve, 100));
					}
				} catch (pageError) {
					console.error(`Error fetching page ${page}:`, pageError);
					hasMore = false;
				}
			}
			// Filter compressed assets first to reduce processing load
			const compressedAssets = allAssets.filter(
				(item: any) => item && item.compression && item.compression.compressed
			);
			// Update total count with actual compressed NFTs count
			setProgress((prev) => ({
				...prev,
				totalCount: compressedAssets.length,
				progress: 80,
			}));

			// Choose chunk size based on collection size
			const chunkSize =
				compressedAssets.length > LARGE_COLLECTION_THRESHOLD
					? CHUNK_SIZES.LARGE
					: CHUNK_SIZES.STANDARD;

			// Process in chunks
			const { processedNFTs, traits, errorCount } = await processNFTsInChunks(
				compressedAssets,
				chunkSize
			);
			addUniqueTraits(traits);
			setProgress((prev) => ({ ...prev, errorCount }));

			if (!suppressMessages) {
				message.success(`Loaded ${processedNFTs.length} compressed NFTs`);
			}

			if (errorCount > 0) {
				console.warn(
					`${errorCount} compressed NFTs had invalid metadata and were filtered out`
				);
			}

			return processedNFTs;
		} catch (error) {
			console.error("Fetch failed:", error);
			// If the main method fails, try fallback
			return await fetchFallbackMethod(suppressMessages);
		}
	}, [
		signer,
		publicKey,
		processNFTsInChunks,
		addUniqueTraits,
		fetchFallbackMethod,
	]);

	const cancelFetch = useCallback(() => {
		cancelledRef.current = true;
	}, []);

	// Main optimized fetch function - simplified approach
	const fetchCompressedNFTs = useCallback(async (suppressMessages: boolean = false): Promise<DigitalAsset[]> => {
		message.destroy();
		if (!publicKey || !rpcEndpoint) {
			console.warn("Missing requirements:", {
				publicKey: !!publicKey,
				rpcEndpoint: !!rpcEndpoint,
			});
			return [];
		}

		cancelledRef.current = false;
		setIsLoading(true);
		setProgress({
			strategy: "fetch",
			progress: 0,
			totalCount: 0,
			errorCount: 0,
		});

		// Only show loading message if not in silent mode
		if (!suppressMessages && !silent) {
			message.loading("Loading compressed NFTs...");
		}

		try {
			// Just start fetching - no need for count check
			const result = await fetchAllCompressedNFTs(suppressMessages || silent);
			setProgress((prev) => ({ ...prev, strategy: "complete" }));
			return result;
		} catch (error) {
			console.error("Failed to fetch NFTs:", error);
			if (!suppressMessages && !silent) {
				message.error("Failed to load NFTs. Please try again.");
			}
			setProgress((prev) => ({ ...prev, strategy: "complete" }));
			return [];
		} finally {
			setIsLoading(false);
			setProgress((prev) => ({ ...prev, progress: 100 }));
		}
	}, [publicKey, rpcEndpoint, fetchAllCompressedNFTs, silent]);

	return {
		fetchCompressedNFTs,
		cancelFetch,
		progress,
		isLoading,
	};
};
